import Link, {LINK_THEME} from 'components/Link';
import {Typography} from 'components/Typography';
import {OnlineChatService} from 'modules/online-chat/services/OnlineChatService';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Drawer} from 'components/Drawer';
import style from './style.module.scss';
import {Collapse} from 'components/Collapse';
import classNames from 'classnames';
import {MENU_SERVICES, MOCK_MENU_CATEGORIES} from 'core/constants';
import {CitiesModal} from 'components/Header/BasicHeader/components/CitiesModal';
import {useUserCity} from 'modules/locations/hooks/useUserCity';
import {ICity} from 'modules/locations/models';
import {useUserCityId} from 'modules/locations/hooks/useUserCityId';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {applyCity, changeLocation} from 'modules/locations/thunks';
import Icon from 'components/Icon';
import {useShowAuthDialog} from 'modules/auth/hooks/useShowAuthDialog';
import {usePersonalPrivateRoutes} from 'core/modules/routing/hooks/private/usePersonalPrivateRoutes';
import {usePriveLinkClickGtag} from 'hooks/analytics/gtag/usePriveLinkClickGtag';
import {useGetUserQuery} from 'modules/user/services';
import {APP_LIST} from 'modules/footer/components/Footer/constants';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {selectFooterSocialNetworks} from 'modules/footer/slice';
import {getFooterChatList} from 'modules/footer/helpers';

interface IMenuItem {
    name: string;
    categories: any[];
    url?: string;
    code?: string;
    isMarked?: boolean;
}

interface IMenuProps {
    isCitiesMenuOpen: boolean;
    isMenuOpen: boolean;
    isBurgerHidden?: boolean;
    setIsMenuOpen: (isMenuOpen: boolean) => void;
    setIsCitiesMenuOpen: (isMenuOpen: boolean) => void;
}

// eslint-disable-next-line max-lines-per-function,complexity
export const Menu = ({
    isCitiesMenuOpen,
    isMenuOpen,
    isBurgerHidden = false,
    setIsMenuOpen,
    setIsCitiesMenuOpen,
}: IMenuProps) => {
    const dispatch = useAppDispatch();

    const {data: user} = useGetUserQuery();
    const isLoggedIn = Boolean(user?.isAuthorized);
    const userCityId = useUserCityId();
    const city = useUserCity();
    const cityName = city?.title || '';

    const socialNets = useAppSelector(selectFooterSocialNetworks);
    const telegramLink = useMemo(
        () =>
            socialNets?.find((item) => 'telegram' === item.code && Boolean(item.link) && 168 === item.type)?.link || '',
        [socialNets],
    );
    const phoneLink = useMemo(
        () => socialNets?.find((item) => 'phone' === item.code && Boolean(item.link))?.link || '',
        [socialNets],
    );

    const footerChatList = useMemo(() => (socialNets ? getFooterChatList(socialNets) : null), [socialNets]);

    const [selectedCityId, setSelectedCityId] = useState<ICity['id'] | null>();

    const {
        inviteFriend: {url: inviteFriend},
        favorites: {url: favorites},
        lk: {url: lk},
    } = usePersonalPrivateRoutes();

    const userFirstName = user?.firstName || '';
    const userLastName = user?.lastName || '';

    const userName = userFirstName && userLastName ? `${userLastName} ${userFirstName}` : 'Профиль';
    const userPhotoSrc = user?.photo ?? '';

    useEffect(() => {
        if (userCityId) {
            setSelectedCityId(userCityId);
        }
    }, [userCityId]);

    const handleOpenCities = () => {
        setIsCitiesMenuOpen?.(true);
    };

    const handleCityChange = useCallback(
        (cityId: number) => {
            setSelectedCityId(cityId);

            dispatch(changeLocation(cityId));

            dispatch(applyCity);

            setIsCitiesMenuOpen?.(false);
        },
        [dispatch, setIsCitiesMenuOpen],
    );

    const handleShowAuthDialog = useShowAuthDialog();

    const handleLoginClick = () => {
        setIsMenuOpen?.(false);
        handleShowAuthDialog();
    };

    const handleProfileClick = () => {
        setIsMenuOpen?.(false);
        // TODO: Вернуть при полноценном переезде на next и удалить window.location.assign
        // router.push(lk());
        window.location.assign(lk());
    };

    const handleFavoritesClick = () => {
        setIsMenuOpen?.(false);
        // TODO: Вернуть при полноценном переезде на next и удалить window.location.assign
        // router.push(favorites());
        window.location.assign(favorites());
    };

    const onInviteClick = () => {
        setIsMenuOpen?.(false);
        // TODO: Вернуть при полноценном переезде на next и удалить window.location.assign
        // router.push(inviteFriend());
        window.location.assign(inviteFriend());
    };

    const priveLinkClickGtag = usePriveLinkClickGtag();

    const renderServices = useMemo(() => {
        return MENU_SERVICES.map((item, index) => {
            const handleClick = item.isPrive ? priveLinkClickGtag : undefined;
            return (
                <li className={style.menuListItem} key={index}>
                    <Link href={item.url} theme={'black'} onClick={handleClick}>
                        <Typography className={style.notCollapsedItem} variant={'h5-medium'}>
                            {item.name}
                        </Typography>
                    </Link>
                </li>
            );
        });
    }, [priveLinkClickGtag]);

    const renderNestedItems = useMemo(
        () =>
            ({categories}: IMenuItem) => {
                return (
                    <ul className={style.menuCategoriesList}>
                        {categories.map(({name, url}, index) => {
                            const isAllBrands = 'Все бренды' === name;
                            return (
                                <li className={style.menuCategoryItem} key={`${name}-${index}`}>
                                    <a href={url} onClick={() => setIsMenuOpen?.(false)}>
                                        <span>
                                            <Typography
                                                className={style.menuLink}
                                                color={isAllBrands ? 'primary100' : 'gray100'}
                                                variant={'body-16-r'}
                                            >
                                                {name}
                                            </Typography>
                                        </span>
                                    </a>
                                </li>
                            );
                        })}
                    </ul>
                );
            },
        [setIsMenuOpen],
    );

    const renderItems = useMemo(() => {
        return MOCK_MENU_CATEGORIES.map((item: IMenuItem, index) => {
            const {categories, name} = item;
            const isDiscount = 'promos' === item.code;
            const isMarked = item.isMarked;

            return (
                <li key={index} className={style.menuListItem}>
                    {Boolean(categories?.length) ? (
                        <Collapse
                            title={
                                <div className={style.title}>
                                    <Typography color={'gray100'} variant={'h5-medium'}>
                                        {item.name}
                                    </Typography>
                                    {isMarked && <span className={classNames('mark', style.mark)} />}
                                </div>
                            }
                        >
                            {renderNestedItems(item)}
                        </Collapse>
                    ) : (
                        <Link
                            className={classNames(style.menuLink, {
                                [style.menuItemDiscount]: isDiscount,
                            })}
                            href={item.url}
                            legacyBehavior={true}
                            onClick={() => setIsMenuOpen?.(false)}
                            passHref={true}
                            theme={LINK_THEME.black}
                        >
                            <Typography
                                className={style.notCollapsedItem}
                                color={isDiscount ? 'primary100' : 'gray100'}
                                variant={'h5-medium'}
                            >
                                {name}
                            </Typography>
                            {isDiscount && <Icon icon={'sale-sm'} />}
                        </Link>
                    )}
                </li>
            );
        });
    }, [renderNestedItems, setIsMenuOpen]);

    return (
        <>
            {!isBurgerHidden && (
                <button className={style.burgerMenu} onClick={() => setIsMenuOpen?.(true)} type={'button'}>
                    <Icon className={style.burgerIcon} icon={'burger'} />
                </button>
            )}
            <Drawer isClosable={true} isOpen={isMenuOpen} onClose={() => setIsMenuOpen?.(false)}>
                <div className={style.content}>
                    <ul className={style.menuList}>
                        {isLoggedIn ? (
                            <>
                                <li className={style.menuItem}>
                                    <button className={style.menuItemContainer} onClick={handleProfileClick}>
                                        {userPhotoSrc ? (
                                            <img alt={'user photo'} loading={'lazy'} src={userPhotoSrc} />
                                        ) : (
                                            <Icon icon={'profile'} />
                                        )}

                                        <Typography variant={'h5-medium'}>{userName}</Typography>
                                    </button>
                                </li>
                                <li className={style.menuItem}>
                                    <button className={style.menuItemContainer} onClick={handleFavoritesClick}>
                                        <Icon icon={'favourite'} />
                                        <Typography variant={'h5-medium'}>Избранное</Typography>
                                    </button>
                                </li>
                            </>
                        ) : (
                            <>
                                <li className={style.menuItem}>
                                    <button className={style.menuItemContainer} onClick={handleLoginClick}>
                                        <Icon icon={'profile'} />
                                        <Typography variant={'h5-medium'}>Личный кабинет</Typography>
                                    </button>
                                </li>
                            </>
                        )}

                        <li className={style.menuItem}>
                            <button className={style.menuItemContainer} onClick={handleOpenCities}>
                                <div className={style.menuItemLocation}>
                                    <Icon icon={'location'} />
                                    <Typography variant={'h5-medium'}>{cityName}</Typography>
                                </div>
                            </button>
                        </li>
                        <li className={style.menuItem}>
                            <Link
                                className={style.menuItemContainer}
                                href={'/nashi-vinoteki'}
                                onClick={() => setIsMenuOpen?.(false)}
                                theme={'black'}
                            >
                                <Icon icon={'placemark'} />
                                <Typography variant={'h5-medium'}>Винотеки</Typography>
                            </Link>
                        </li>
                    </ul>
                    {isLoggedIn && (
                        <a className={style.inviteContainer} onClick={onInviteClick}>
                            <Icon icon={'friend'} />
                            <Typography className={style.inviteText} element={'div'} variant={'h5-medium'}>
                                Приведи друга
                            </Typography>
                        </a>
                    )}

                    <ul className={style.menuItemsList}>{renderItems}</ul>
                    <ul className={style.menuItemsList}>{renderServices}</ul>
                    <div className={style.menuSocialList}>
                        <div>
                            {footerChatList?.title && (
                                <Typography className={style.menuSocialTitle} element={'div'} variant={'p-medium'}>
                                    {footerChatList.title}
                                </Typography>
                            )}
                            <div className={style.appIconsList}>
                                <div data-share={'telegram'}>
                                    <Link href={telegramLink} theme={'black'}>
                                        <Icon icon={'Telegram-xl'} />
                                    </Link>
                                </div>
                                <div data-share={'messenger'}>
                                    <button onClick={() => OnlineChatService.openChat()}>
                                        <Icon icon={'Footer-chat'} />
                                    </button>
                                </div>
                                <div data-share={'phone'}>
                                    <Link href={phoneLink} theme={'black'}>
                                        <Icon icon={'Footer-phone'} />
                                    </Link>
                                </div>
                            </div>
                        </div>
                        <div>
                            <Typography className={style.menuSocialTitle} element={'div'} variant={'p-medium'}>
                                Приложение Simplewine
                            </Typography>
                            <ul className={style.appIconsList}>
                                {APP_LIST('menu').linkList.map((app, index) => {
                                    // console.log(footerChatList);
                                    return (
                                        <li key={`${index}/menu-app`}>
                                            <Link href={app.path} theme={`black`} target={'_blank'}>
                                                {app.icon}
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                </div>
                <CitiesModal
                    checkedLocationId={selectedCityId}
                    isOpen={Boolean(isCitiesMenuOpen)}
                    onSelect={handleCityChange}
                    setOpen={setIsCitiesMenuOpen}
                />
            </Drawer>
        </>
    );
};
